import React, { useEffect, useState, useCallback } from 'react';
import { clientsAPI } from '../utils/api';
import toast from 'react-hot-toast';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const fetchClients = useCallback(() => {
    setLoading(true);
    clientsAPI.getAll({ search })
      .then(res => setClients(res.data))
      .catch(() => toast.error('Failed to load clients'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', address: '', phone: '', email: '' });
    setShowModal(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, address: client.address || '', phone: client.phone || '', email: client.email || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (editing) {
        await clientsAPI.update(editing.id, form);
        toast.success('Client updated');
      } else {
        await clientsAPI.create(form);
        toast.success('Client added');
      }
      setShowModal(false);
      fetchClients();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete client "${name}"?`)) return;
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted');
      fetchClients();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Clients</h1>
        <button className="btn btn-primary" onClick={openNew}>+ Add Client</button>
      </div>

      <div className="search-bar">
        <input
          className="form-control search-input"
          placeholder="Search by name or address..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-text">No clients yet</div>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openNew}>Add First Client</button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-wrapper desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr key={c.id}>
                      <td style={{ color: '#94a3b8', fontSize: 13 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{c.address || '-'}</td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{c.phone || '-'}</td>
                      <td style={{ color: '#64748b', fontSize: 13 }}>{c.email || '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id, c.name)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-card-list" style={{ padding: '12px' }}>
              {clients.map((c, i) => (
                <div className="mobile-card" key={c.id}>
                  <div className="mobile-card-row">
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>#{i + 1}</span>
                  </div>
                  {c.address && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Address</span>
                      <span className="mobile-card-value">{c.address}</span>
                    </div>
                  )}
                  {c.phone && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Phone</span>
                      <span className="mobile-card-value">{c.phone}</span>
                    </div>
                  )}
                  {c.email && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Email</span>
                      <span className="mobile-card-value">{c.email}</span>
                    </div>
                  )}
                  <div className="mobile-card-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id, c.name)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2 className="modal-title">{editing ? 'Edit Client' : 'Add Client'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Full name or company"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  className="form-control"
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="City, Country"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="form-control"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+880..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}